import React, { FC, useState, useEffect, useContext } from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  Button,
  CircularProgress,
} from '@mui/material';
import { format } from 'date-fns';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';

import { ProjectCommunication } from '../../model';
import axios from '../../../sharedModules/utils/axios.utils';
import { AuthenticationContext } from '../../../homeModules/auth/contexts/AuthenticationContext';

interface ProjectCommunicationUpdateFormProps {
  communication: ProjectCommunication;
  onSuccess: () => void;
  onCancel?: () => void;
  showCancelButton?: boolean;
}

interface ExecutionStatusesResponse {
  statuses: string[];
}

const ProjectCommunicationUpdateForm: FC<ProjectCommunicationUpdateFormProps> = ({
  communication,
  onSuccess,
  onCancel,
  showCancelButton = false,
}) => {
  const { user } = useContext(AuthenticationContext);
  const userName = user?.name || user?.last_name_first || `${user?.first_name} ${user?.last_name}` || 'Unknown User';

  const [formValues, setFormValues] = useState({
    DATE_CHNG: format(new Date(), 'MM/dd/yyyy'),
    TIME_CHNG: format(new Date(), 'HH:mm:ss'),
    CHNGED_BY: userName,
    CMPLT_DATE: '',
    U_EXSTATUS: '',
    NEW_NOTES: '',
    JOB_NOTES: '',
  });
  const [originalExecutionStatus, setOriginalExecutionStatus] = useState<string>('');
  const [executionStatuses, setExecutionStatuses] = useState<string[]>([
    'Booking',
    'Turnover',
    'Engineering',
    'Equipment Ordered',
    'Scope Scheduled',
    'Execution',
    'Subcontractor',
    'Startup',
    'Punchlist',
    'Closeout',
    'Job Complete',
    'PO Outstanding',
    'Warranty',
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (communication) {
      const currentExStatus = communication.status || '';
      setOriginalExecutionStatus(currentExStatus);
      setFormValues({
        DATE_CHNG: format(new Date(), 'MM/dd/yyyy'),
        TIME_CHNG: format(new Date(), 'HH:mm:ss'),
        CHNGED_BY: user?.servman_user_id || userName,
        CMPLT_DATE: '',
        U_EXSTATUS: currentExStatus,
        NEW_NOTES: '',
        JOB_NOTES: '',
      });
      setError('');
      setSuccess(false);
    }
  }, [communication, userName, user]);

  useEffect(() => {
    const fetchExecutionStatuses = async () => {
      try {
        const response = await axios.get<ExecutionStatusesResponse>('/admin/job_execution_statuses');
        if (response?.data?.statuses && response.data.statuses.length > 0) {
          setExecutionStatuses(response.data.statuses);
        }
      } catch (err) {
        console.log('Using default execution statuses');
      }
    };
    fetchExecutionStatuses();
  }, []);

  const handleSubmit = async () => {
    if (!communication) return;

    if (!formValues.CHNGED_BY.trim()) {
      setError('Changed By is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const updatedFormValues = { ...formValues };

      await axios.patch(`/customer_issues/servman_project_communications/${communication.recno}`, {
        project_communication: updatedFormValues,
      });

      setSuccess(true);
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (err: any) {
      console.log(err);
      setError(err.response?.data?.error || 'Failed to update project communication');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>
          Project communication updated successfully!
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, mt: 2 }}>
            Update Job Execution Status
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Job Execution Status</InputLabel>
            <Select
              value={formValues.U_EXSTATUS}
              label="Job Execution Status"
              onChange={(e) =>
                setFormValues({
                  ...formValues,
                  U_EXSTATUS: e.target.value,
                })
              }
              disabled={loading || success}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {executionStatuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {formValues.U_EXSTATUS !== originalExecutionStatus && (
            <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block' }}>
              Status change will be automatically recorded in notes
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, mt: 3 }}>
            Update Communication
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Update Communication"
            fullWidth
            multiline
            rows={4}
            value={formValues.NEW_NOTES}
            onChange={(e) => setFormValues({ ...formValues, NEW_NOTES: e.target.value })}
            disabled={loading || success}
            helperText="Update communication will be prepended to the top of previous notes"
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, mt: 3 }}>
            Update Job Notes
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Update Job Notes"
            fullWidth
            multiline
            rows={4}
            value={formValues.JOB_NOTES}
            onChange={(e) => setFormValues({ ...formValues, JOB_NOTES: e.target.value })}
            disabled={loading || success}
            helperText="Update job notes will be prepended to the top of previous job notes"
          />
        </Grid>
      </Grid>

      <Box
        sx={{ mt: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 1, border: '1px solid', borderColor: 'info.light' }}
      >
        <Typography variant="caption" color="text.secondary">
          <strong>Note:</strong> Changes will be synced to ServMan. Updates may take a few minutes to appear in the
          system.
        </Typography>
      </Box>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        {showCancelButton && onCancel && (
          <Button onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
        )}
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || success}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
};

export default ProjectCommunicationUpdateForm;
